import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./FormClientContract.css";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { NumericFormat } from "react-number-format";
import { useDropzone } from "react-dropzone";

const animatedComponents = makeAnimated();

const selectPortalStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
    menu: (base) => ({ ...base, zIndex: 999999 }),
};

const formatDateLabel = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    const date = new Date(year, month - 1, day);

    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const formatAmountDisplay = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0,00";

    return new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
};

const STATUS_OPTIONS = [
    { value: "Active", label: "Active" },
    { value: "Completed", label: "Completed" },
    { value: "Suspended", label: "Suspended" },
    { value: "Cancelled", label: "Cancelled" },
];

const CalendarIcon = () => (
    <span
        className="calendar-icon"
        style={{
            position: "absolute",
            right: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
        }}
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
    </span>
);

const EditClientAddendum = () => {
    const navigate = useNavigate();
    const { addendumId, id, pk } = useParams();
    const clientAddendumId = addendumId || id || pk;

    const [parentContract, setParentContract] = useState(null);
    const [contractId, setContractId] = useState(null);

    const [clients, setClients] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [contractOpportunities, setContractOpportunities] = useState([]);
    const [projectCodeOptions, setProjectCodeOptions] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [exchangeRates, setExchangeRates] = useState([]);
    const [systemCurrencyId, setSystemCurrencyId] = useState(null);
    const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");

    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedProjectCode, setSelectedProjectCode] = useState(null);
    const [selectedResponsible, setSelectedResponsible] = useState(null);
    const [selectedOpps, setSelectedOpps] = useState([]);
    const [selectedSignDate, setSelectedSignDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const [files, setFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [dropzoneKey, setDropzoneKey] = useState(Date.now());

    const [errorNotification, setErrorNotification] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isOppLoading, setIsOppLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [expandedSections, setExpandedSections] = useState({
        contract: true,
        opportunities: true,
        attachments: true,
    });

    const [formData, setFormData] = useState({
        nr: "",
        client_document: "",
        sign_date: "",
        end_date: "",
        responsible_person: "",
        client: "",
        project_code: "",
        object: "",
        status: "",
    });

    const [lines, setLines] = useState([]);

    const isFrameworkAgreement =
        parentContract?.contract_type === "Framework Agreement";

    const RAW_API_BASE =
        process.env.REACT_APP_API_URL ||
        axiosInstance.defaults.baseURL ||
        window.location.origin;

    const API_BASE_URL = String(RAW_API_BASE)
        .replace(/\/api\/?$/, "")
        .replace(/\/+$/, "");

    const showError = useCallback((message, timeout = 5000) => {
        setErrorNotification(message);
        setTimeout(() => setErrorNotification(""), timeout);
    }, []);

    const exchangeOptionsByCurrencyId = useMemo(() => {
        const m = new Map();

        for (const r of exchangeRates) {
            const curId = r.currency;
            if (!m.has(curId)) m.set(curId, []);

            m.get(curId).push({
                value: r.id,
                label: String(r.value ?? ""),
                dateLabel: r.date || "",
                rateValue: Number(r.value),
                rateDate: r.date,
            });
        }

        for (const [k, arr] of m.entries()) {
            arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
            m.set(k, arr);
        }

        return m;
    }, [exchangeRates]);

    const getDefaultExchangeRateOption = useCallback(
        (currencyId) => {
            const arr = exchangeOptionsByCurrencyId.get(currencyId) || [];
            return arr.length ? arr[0] : null;
        },
        [exchangeOptionsByCurrencyId]
    );

    const computeSystemAmount = useCallback(
        (sum, currencyId, exchangeOpt) => {
            if (sum == null || sum === "") return "";

            const s = Number(sum);
            if (!Number.isFinite(s)) return "";

            if (!currencyId || !systemCurrencyId) return "";

            if (Number(currencyId) === Number(systemCurrencyId)) return s.toFixed(2);

            const rateVal = exchangeOpt?.rateValue;
            if (!rateVal || !Number.isFinite(rateVal)) return "";

            return (s * rateVal).toFixed(2);
        },
        [systemCurrencyId]
    );

    const totalAddendumDisplay = useMemo(() => {
        if (!lines.length) return "0,00";

        const validLines = lines.filter((line) => {
            const hasTotal =
                line.total !== null && line.total !== undefined && line.total !== "";
            const hasCurrency = !!line.currency?.value;
            return hasTotal && hasCurrency;
        });

        if (!validLines.length) return "0,00";

        const distinctCurrencyIds = [
            ...new Set(validLines.map((line) => line.currency.value)),
        ];

        if (distinctCurrencyIds.length === 1) {
            const total = validLines.reduce((acc, line) => {
                const val = Number(line.total);
                return acc + (Number.isFinite(val) ? val : 0);
            }, 0);

            const currencyLabel = validLines[0]?.currency?.label || "";
            return `${formatAmountDisplay(total)} ${currencyLabel}`.trim();
        }

        const totalSystem = validLines.reduce((acc, line) => {
            const val = Number(line.system_total);
            return acc + (Number.isFinite(val) ? val : 0);
        }, 0);

        return `${formatAmountDisplay(totalSystem)} ${systemCurrencyLabel || ""}`.trim();
    }, [lines, systemCurrencyLabel]);

    const getFileUrl = (file) => {
        if (!file) return null;
        if (file.file_url) return file.file_url;
        if (file.url) return file.url;

        if (file.file) {
            const rawPath = String(file.file);

            if (rawPath.startsWith("http://") || rawPath.startsWith("https://")) {
                return rawPath;
            }

            const normalizedPath = rawPath.startsWith("/")
                ? rawPath
                : `/media/${rawPath.replace(/^media\//, "")}`;

            return `${API_BASE_URL}${normalizedPath}`;
        }

        return null;
    };

    const getExistingFileName = (file) => {
        if (file?.filename) return file.filename;

        if (typeof file?.file === "string") {
            const parts = file.file.split("/");
            return parts[parts.length - 1];
        }

        return "download";
    };

    const isPdfExistingFile = (file) => {
        const filename = getExistingFileName(file).toLowerCase();
        return filename.endsWith(".pdf");
    };

    const getFileIcon = (file, isExisting = false) => {
        if (isExisting) {
            const filename = getExistingFileName(file).toLowerCase();

            if (filename.endsWith(".pdf")) {
                return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
            }

            if (filename.endsWith(".doc") || filename.endsWith(".docx")) {
                return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
            }

            if (filename.endsWith(".xls") || filename.endsWith(".xlsx")) {
                return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
            }

            return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
        }

        if (file.type === "application/pdf") {
            return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
        }

        if (
            file.type === "application/msword" ||
            file.type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) {
            return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
        }

        if (
            file.type === "application/vnd.ms-excel" ||
            file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
            return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
        }

        return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
    };

    const handleFileClick = async (file, isExisting = false) => {
        if (isExisting) {
            const fileUrl = getFileUrl(file);

            if (!fileUrl) {
                showError("File URL not available.");
                return;
            }

            try {
                const response = await fetch(fileUrl);
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                const filename = getExistingFileName(file);

                if (isPdfExistingFile(file)) {
                    const newWindow = window.open();

                    if (!newWindow) {
                        showError("Popup blocked. Please allow popups for PDF preview.");
                        URL.revokeObjectURL(objectUrl);
                        return;
                    }

                    newWindow.document.write(`
            <html>
              <head>
                <title>${filename}</title>
                <style>
                  body { margin: 0; height: 100vh; }
                  embed { width: 100%; height: 100%; }
                </style>
              </head>
              <body>
                <embed src="${objectUrl}" type="application/pdf" />
              </body>
            </html>
          `);
                    newWindow.document.close();

                    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                } else {
                    const a = document.createElement("a");
                    a.href = objectUrl;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
                }
            } catch (error) {
                console.error("Existing file open error:", error);
                showError("Could not open/download the selected file.");
            }

            return;
        }

        const url = URL.createObjectURL(file);

        if (file.type === "application/pdf") {
            const newWindow = window.open();

            if (!newWindow) {
                showError("Popup blocked. Please allow popups for PDF preview.");
                URL.revokeObjectURL(url);
                return;
            }

            newWindow.document.write(`
        <html>
          <head>
            <title>${file.name}</title>
            <style>
              body { margin: 0; height: 100vh; }
              embed { width: 100%; height: 100%; }
            </style>
          </head>
          <body>
            <embed src="${url}" type="application/pdf" />
          </body>
        </html>
      `);
            newWindow.document.close();

            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
            const a = document.createElement("a");
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);

            try {
                const [
                    addendumRes,
                    clientsRes,
                    employeesRes,
                    currenciesRes,
                    exchangeRes,
                    systemRes,
                ] = await Promise.all([
                    axiosInstance.get(`/client-addendums/${clientAddendumId}/`),
                    axiosInstance.get("/clients/"),
                    axiosInstance.get("/employees/"),
                    axiosInstance.get("/currencies/"),
                    axiosInstance.get("/exchange-rate/"),
                    axiosInstance.get("/system-currency/"),
                ]);

                const addendum = addendumRes.data;

                const normalizedContractId =
                    typeof addendum.contract === "object"
                        ? addendum.contract?.id
                        : addendum.contract_id ?? addendum.contract ?? "";

                setContractId(normalizedContractId);

                let parentData = null;

                if (normalizedContractId) {
                    const parentRes = await axiosInstance.get(
                        `/client-contracts/${normalizedContractId}/`
                    );

                    parentData = parentRes.data;
                    setParentContract(parentData);
                }

                const formattedClients = (clientsRes.data || []).map((c) => ({
                    value: c.id,
                    label: c.name ?? `Client ${c.id}`,
                }));

                const formattedEmployees = (employeesRes.data || []).map((emp) => ({
                    value: emp.id,
                    label:
                        `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
                        `Employee ${emp.id}`,
                }));

                const formattedCurrencies = (currenciesRes.data || []).map((curr) => ({
                    value: curr.id,
                    label: curr.abbreviation,
                    abbreviation: curr.abbreviation,
                }));

                const sortedExchangeRates = (exchangeRes.data || []).sort(
                    (a, b) => new Date(b.date) - new Date(a.date)
                );

                setClients(formattedClients);
                setEmployees(formattedEmployees);
                setCurrencies(formattedCurrencies);
                setExchangeRates(sortedExchangeRates);

                let currentSystemCurrencyId = null;

                if (Array.isArray(systemRes.data) && systemRes.data.length > 0) {
                    currentSystemCurrencyId = systemRes.data[0]?.currency ?? null;
                    setSystemCurrencyId(currentSystemCurrencyId || null);

                    const sys = formattedCurrencies.find(
                        (c) => Number(c.value) === Number(currentSystemCurrencyId)
                    );
                    setSystemCurrencyLabel(sys?.abbreviation || "");
                }

                const normalizedClient =
                    typeof addendum.client === "object"
                        ? addendum.client?.id
                        : addendum.client_id ?? addendum.client ?? "";

                const normalizedResponsible =
                    typeof addendum.responsible_person === "object"
                        ? addendum.responsible_person?.id
                        : addendum.responsible_person_id ??
                        addendum.responsible_person ??
                        "";

                const normalizedLines = Array.isArray(addendum.lines)
                    ? addendum.lines
                    : [];

                const normalizedProjectCode =
                    addendum.project_code_id ||
                    addendum.project_code ||
                    normalizedLines[0]?.project_code ||
                    parentData?.project_code_id ||
                    parentData?.project_code ||
                    "";

                setFormData({
                    nr: addendum.nr ?? "",
                    client_document: addendum.client_document ?? "",
                    sign_date: addendum.sign_date ?? "",
                    end_date: addendum.end_date ?? "",
                    responsible_person: normalizedResponsible
                        ? Number(normalizedResponsible)
                        : "",
                    client: normalizedClient ? Number(normalizedClient) : "",
                    project_code: normalizedProjectCode ? Number(normalizedProjectCode) : "",
                    object: addendum.object ?? "",
                    status: addendum.status ?? "",
                });

                setSelectedSignDate(addendum.sign_date ? parseISO(addendum.sign_date) : null);
                setSelectedEndDate(addendum.end_date ? parseISO(addendum.end_date) : null);

                if (addendum.status) {
                    const statusOption = STATUS_OPTIONS.find(
                        (s) => s.value === addendum.status
                    );
                    if (statusOption) {
                        setSelectedStatus(statusOption);
                    }
                }

                const clientOption = formattedClients.find(
                    (c) => Number(c.value) === Number(normalizedClient)
                );
                setSelectedClient(clientOption || null);

                const responsibleOption = formattedEmployees.find(
                    (e) => Number(e.value) === Number(normalizedResponsible)
                );
                setSelectedResponsible(responsibleOption || null);

                const attachments =
                    addendum.attach_files || addendum.attachments || addendum.files || [];

                setExistingAttachments(
                    Array.isArray(attachments)
                        ? attachments.map((att) => ({
                            id: att.id,
                            filename: att.filename || att.file?.split("/").pop() || "Unknown file",
                            file: att.file,
                            file_url: att.file_url || att.url || null,
                        }))
                        : []
                );

                setSelectedOpps(
                    normalizedLines.map((line) => ({
                        value: Number(line.opportunity),
                        label: line.opportunity_label || `Opportunity ${line.opportunity}`,
                        project_code: line.project_code,
                        project_code_label: line.project_code_label || "",
                        subproject_code: line.subproject_code,
                        subproject_code_label: line.subproject_code_label || "",
                    }))
                );

                setLines(
                    normalizedLines.map((line) => {
                        const currencyOption =
                            formattedCurrencies.find(
                                (c) => Number(c.value) === Number(line.currency)
                            ) || null;

                        const exchangeRateOption =
                            sortedExchangeRates
                                .filter((r) => Number(r.currency) === Number(line.currency))
                                .map((r) => ({
                                    value: r.id,
                                    label: String(r.value ?? ""),
                                    dateLabel: r.date || "",
                                    rateValue: Number(r.value),
                                    rateDate: r.date,
                                }))
                                .find((r) => Number(r.value) === Number(line.exchange_rate)) || null;

                        return {
                            id: line.id,
                            opportunityId: Number(line.opportunity),
                            opportunityLabel:
                                line.opportunity_label || `Opportunity ${line.opportunity}`,
                            subproject_code_text: line.subproject_code_label || "",
                            currency: currencyOption,
                            exchange_rate: exchangeRateOption,
                            total:
                                line.total_opportunities_contract !== null &&
                                    line.total_opportunities_contract !== undefined
                                    ? Number(line.total_opportunities_contract)
                                    : null,
                            system_total:
                                line.total_opp_contract_system !== null &&
                                    line.total_opp_contract_system !== undefined
                                    ? String(line.total_opp_contract_system)
                                    : "",
                        };
                    })
                );
            } catch (error) {
                console.error("Addendum load error:", error.response?.data || error.message);
                showError("Failed to load addendum.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [clientAddendumId, showError]);

    useEffect(() => {
        if (!formData.client || !parentContract || !contractId) {
            setContractOpportunities([]);
            setProjectCodeOptions([]);
            return;
        }

        const inheritedProjectCode =
            parentContract.project_code_id ??
            parentContract.project_code ??
            "";

        const inheritedProjectCodeLabel =
            parentContract.project_code_label || "";

        setIsOppLoading(true);

        // =====================================================
        // FRAMEWORK AGREEMENT
        // Project Code-ul este selectabil
        // Păstrăm Project Code-ul existent al adiționalului
        // =====================================================
        if (parentContract.contract_type === "Framework Agreement") {
            Promise.all([
                axiosInstance.get(
                    `/code_registries/?client=${formData.client}`
                ),
                axiosInstance.get(
                    `/opportunities/?client=${formData.client}&for_contract=1`
                ),
            ])
                .then(([projectCodesResponse, opportunitiesResponse]) => {
                    const projectCodesData = Array.isArray(
                        projectCodesResponse.data
                    )
                        ? projectCodesResponse.data
                        : projectCodesResponse.data?.results || [];

                    const allProjectCodes = projectCodesData
                        .map((pc) => ({
                            value: pc.id,
                            label: pc.code,
                        }))
                        .sort((a, b) =>
                            (a.label || "").localeCompare(b.label || "")
                        );

                    setProjectCodeOptions(allProjectCodes);



                    const opportunitiesData = Array.isArray(
                        opportunitiesResponse.data
                    )
                        ? opportunitiesResponse.data
                        : opportunitiesResponse.data?.results || [];

                    const formatted = opportunitiesData.map((opp) => ({
                        value: opp.id,
                        label:
                            `${opp.code_opportunity ?? ""} - ${opp.title_opportunity ?? ""
                                }`.trim() ||
                            opp.project_name ||
                            `Opportunity ${opp.id}`,
                        project_code: opp.project_code,
                        project_code_label:
                            opp.project_code_label || "",
                        subproject_code: opp.subproject_code,
                        subproject_code_label:
                            opp.subproject_code_label || "",
                        client: opp.client,
                    }));

                    setContractOpportunities(formatted);
                })
                .catch((error) => {
                    console.error(
                        "Framework Agreement data fetch error:",
                        error
                    );

                    setContractOpportunities([]);
                    setProjectCodeOptions([]);
                })
                .finally(() => {
                    setIsOppLoading(false);
                });

            return;
        }

        // =====================================================
        // CONTRACT + PURCHASE ORDER
        // Project Code-ul rămâne cel din contractul părinte
        // =====================================================
        if (inheritedProjectCode) {
            const projectCodeOption = {
                value: Number(inheritedProjectCode),
                label:
                    inheritedProjectCodeLabel ||
                    `Project Code ${inheritedProjectCode}`,
            };

            setProjectCodeOptions([projectCodeOption]);
            setSelectedProjectCode(projectCodeOption);

            setFormData((prev) => ({
                ...prev,
                project_code: Number(inheritedProjectCode),
            }));
        } else {
            setProjectCodeOptions([]);
            setSelectedProjectCode(null);

            setFormData((prev) => ({
                ...prev,
                project_code: "",
            }));
        }

        axiosInstance
            .get(
                `/opportunities/?contract=${contractId}&for_addendum=1`
            )
            .then((response) => {
                const responseData = Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || [];

                const formatted = responseData.map((opp) => ({
                    value: opp.id,
                    label:
                        `${opp.code_opportunity ?? ""} - ${opp.title_opportunity ?? ""
                            }`.trim() ||
                        opp.project_name ||
                        `Opportunity ${opp.id}`,
                    project_code: opp.project_code,
                    project_code_label:
                        opp.project_code_label || "",
                    subproject_code: opp.subproject_code,
                    subproject_code_label:
                        opp.subproject_code_label || "",
                    client: opp.client,
                }));

                setContractOpportunities(formatted);
            })
            .catch((error) => {
                console.error(
                    "Contract opportunities fetch error:",
                    error
                );

                setContractOpportunities([]);
            })
            .finally(() => {
                setIsOppLoading(false);
            });
    }, [
        formData.client,
        parentContract,
        contractId,
    ]);

    useEffect(() => {
        if (!formData.project_code || !projectCodeOptions.length) return;

        const projectCodeOption = projectCodeOptions.find(
            (opt) => Number(opt.value) === Number(formData.project_code)
        );

        if (projectCodeOption) {
            setSelectedProjectCode(projectCodeOption);
        }
    }, [formData.project_code, projectCodeOptions]);

    const filteredOpportunitiesByProjectCode = useMemo(() => {
        if (!isFrameworkAgreement) {
            return contractOpportunities;
        }

        if (!selectedProjectCode) {
            return contractOpportunities;
        }

        return contractOpportunities.filter(
            (opp) =>
                Number(opp.project_code) ===
                Number(selectedProjectCode.value)
        );
    }, [
        contractOpportunities,
        selectedProjectCode,
        isFrameworkAgreement,
    ]);

    const handleProjectCodeChange = (selectedOption) => {
        setSelectedProjectCode(selectedOption);

        // La schimbarea Project Code-ului eliminăm oportunitățile
        // selectate pe vechiul Project Code.
        setSelectedOpps([]);
        setLines([]);

        setFormData((prev) => ({
            ...prev,
            project_code: selectedOption
                ? selectedOption.value
                : "",
        }));

        setFieldErrors((prev) => ({
            ...prev,
            project_code: "",
            lines: "",
        }));
    };

    const updateLine = (opportunityId, patch) => {
        setLines((prev) =>
            prev.map((l) => {
                if (l.opportunityId !== opportunityId) return l;

                const next = { ...l, ...patch };
                const currencyId = next.currency?.value ?? null;

                if (
                    currencyId &&
                    systemCurrencyId &&
                    Number(currencyId) !== Number(systemCurrencyId)
                ) {
                    if (!next.exchange_rate) {
                        next.exchange_rate = getDefaultExchangeRateOption(currencyId);
                    }
                } else {
                    next.exchange_rate = null;
                }

                next.system_total = computeSystemAmount(
                    next.total,
                    currencyId,
                    next.exchange_rate
                );

                return next;
            })
        );
    };

    const handleOppsMultiChange = (opts) => {
        const selected = opts || [];
        setSelectedOpps(selected);

        setLines((prevLines) => {
            const prevById = new Map(prevLines.map((l) => [l.opportunityId, l]));
            const next = [];

            for (const o of selected) {
                const existing = prevById.get(o.value);

                if (existing) {
                    next.push(existing);
                } else {
                    next.push({
                        opportunityId: o.value,
                        opportunityLabel: o.label,
                        subproject_code_text: o.subproject_code_label || "",
                        currency: null,
                        exchange_rate: null,
                        total: null,
                        system_total: "",
                    });
                }
            }

            return next;
        });

        setFieldErrors((prev) => ({
            ...prev,
            lines: "",
        }));
    };

    const removeOpportunity = (opportunityId) => {
        setSelectedOpps((prev) => prev.filter((o) => o.value !== opportunityId));
        setLines((prev) => prev.filter((l) => l.opportunityId !== opportunityId));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            "application/pdf": [],
            "application/msword": [],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
            "image/jpeg": [],
            "image/png": [],
            "application/vnd.ms-excel": [],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
            "text/plain": [],
        },
        multiple: true,
        maxSize: 1024 * 1024 * 100,
        onDrop: (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                const tooLarge = rejectedFiles.filter(
                    (f) => f.errors[0]?.code === "file-too-large"
                );

                if (tooLarge.length > 0) {
                    showError(`File ${tooLarge[0].file.name} exceeds 100MB.`, 3000);
                }
            }

            if (acceptedFiles.length > 0) {
                setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
            }
        },
    });

    const handleRemoveFile = (indexToRemove, isExisting = false) => {
        if (isExisting) {
            setExistingAttachments((prev) =>
                prev.filter((_, index) => index !== indexToRemove)
            );
            return;
        }

        setFiles((prevFiles) => {
            const updatedFiles = prevFiles.filter((_, index) => index !== indexToRemove);

            if (updatedFiles.length === 0) {
                setDropzoneKey(Date.now());
            }

            return updatedFiles;
        });
    };

    const validateFiles = () => {
        const MAX_FILE_SIZE = 100 * 1024 * 1024;
        const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

        if (oversizedFiles.length > 0) {
            showError(
                `The following files exceed the 100MB limit:\n${oversizedFiles
                    .map((f) => f.name)
                    .join("\n")}`,
                5000
            );
            return false;
        }

        return true;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nr?.trim()) newErrors.nr = "Required";
        if (!formData.client_document?.trim()) newErrors.client_document = "Required";
        if (!formData.responsible_person) newErrors.responsible_person = "Required";
        if (!formData.client) newErrors.client = "Required";
        if (!isFrameworkAgreement && !formData.project_code) {
            newErrors.project_code = "Required";
        }
        if (!formData.sign_date) newErrors.sign_date = "Required";
        if (!formData.end_date) newErrors.end_date = "Required";
        if (!formData.object?.trim()) newErrors.object = "Required";
        if (!isFrameworkAgreement && !lines.length) {
            newErrors.lines = "Select at least one opportunity";
        }

        lines.forEach((l, idx) => {
            if (l.total !== null && l.total !== undefined && l.total !== "") {
                if (!l.currency) {
                    newErrors[`line_${idx}_currency`] = "Currency required";
                }

                const currencyId = l.currency?.value ?? null;

                if (
                    currencyId &&
                    systemCurrencyId &&
                    Number(currencyId) !== Number(systemCurrencyId) &&
                    !l.exchange_rate
                ) {
                    newErrors[`line_${idx}_exchange_rate`] = "Exchange rate required";
                }
            } else {
                newErrors[`line_${idx}_total`] = "Total required";
            }
        });

        setFieldErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;
        if (!validateFiles()) return;
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const submissionData = new FormData();

            submissionData.append("contract", String(contractId || ""));
            submissionData.append("nr", formData.nr);
            submissionData.append("client_document", formData.client_document);
            submissionData.append("sign_date", formData.sign_date);
            submissionData.append("end_date", formData.end_date);
            submissionData.append(
                "responsible_person",
                String(formData.responsible_person || "")
            );
            submissionData.append("client", String(formData.client || ""));
            if (formData.project_code) {
                submissionData.append(
                    "project_code",
                    String(formData.project_code)
                );
            } else if (isFrameworkAgreement) {
                submissionData.append("project_code", "");
            }

            submissionData.append("object", formData.object);
            submissionData.append("status", formData.status || "Active");

            const linesPayload = lines.map((line) => ({
                opportunity: Number(line.opportunityId),
                currency: line.currency ? Number(line.currency.value) : null,
                exchange_rate: line.exchange_rate ? Number(line.exchange_rate.value) : null,
                total_opportunities_contract:
                    line.total === null || line.total === "" ? null : Number(line.total),
            }));

            submissionData.append("lines", JSON.stringify(linesPayload));

            existingAttachments.forEach((attachment) => {
                if (attachment.id) {
                    submissionData.append("keep_attachments", attachment.id);
                }
            });

            files.forEach((file) => {
                submissionData.append("attachments", file);
            });

            await axiosInstance.put(`/client-addendums/${clientAddendumId}/`, submissionData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            navigate(`/client_contracts/contracts-history/${contractId}`, {
                replace: true,
                state: {
                    selectedAddendumId: Number(clientAddendumId),
                },
            });
        } catch (error) {
            console.error("Save error:", error.response?.data || error.message);

            if (error.response?.data) {
                const err = error.response.data;

                if (
                    err.attachments ||
                    (typeof err === "object" &&
                        JSON.stringify(err).toLowerCase().includes("file"))
                ) {
                    showError("File upload error. Please check file type and size.");
                } else if (typeof err === "object") {
                    const firstMessage =
                        Object.values(err).flat().join(" | ") || JSON.stringify(err);
                    showError(firstMessage);
                } else {
                    showError("An error occurred while saving.");
                }
            } else {
                showError("An error occurred while saving.");
            }

            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(false);
    };

    const ErrorMessage = ({ error }) =>
        error ? (
            <div className="FormClientContract-error">
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
            </div>
        ) : null;

    const SectionHeader = ({ title, section }) => (
        <div
            className="FormClientContract-section-header"
            onClick={() =>
                setExpandedSections((prev) => ({
                    ...prev,
                    [section]: !prev[section],
                }))
            }
        >
            <h2>{title}</h2>
            <button type="button" className="toggle-btn">
                {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </button>
        </div>
    );

    if (isLoading) {
        return (
            <div className="FormClientContract">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading addendum form...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="FormClientContract">
            {errorNotification && (
                <div className="FormClientContract-top-error">{errorNotification}</div>
            )}

            <div className="FormClientContract-header">
                <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
                <h1 className="header-title">EDIT CLIENT ADDENDUM</h1>
                <div className="contract-number-pill">
                    Contract: {parentContract?.nr || "-"}
                </div>
            </div>

            <hr className="title-separator" />

            <form onSubmit={handleSubmit} className="FormClientContract-form">
                <div className="FormClientContract-section">
                    <SectionHeader title="ADDENDUM DETAILS" section="contract" />

                    {expandedSections.contract && (
                        <div className="section-content">
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">
                                        PMPC Addendum Nr. <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.nr}
                                        onChange={(e) =>
                                            setFormData((prev) => ({ ...prev, nr: e.target.value }))
                                        }
                                        className="FormClientContract-input"
                                        autoComplete="off"
                                    />
                                    <ErrorMessage error={fieldErrors.nr} />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Client Document <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.client_document}
                                        onChange={(e) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                client_document: e.target.value,
                                            }));
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                client_document: "",
                                            }));
                                        }}
                                        className="FormClientContract-input"
                                        autoComplete="off"
                                        placeholder="Enter client document..."
                                    />
                                    <ErrorMessage error={fieldErrors.client_document} />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Responsible Person <span className="required">*</span>
                                    </label>
                                    <Select
                                        options={employees}
                                        classNamePrefix="FormClientContract-select"
                                        value={selectedResponsible}
                                        onChange={(selectedOption) => {
                                            setSelectedResponsible(selectedOption);
                                            setFormData((prev) => ({
                                                ...prev,
                                                responsible_person: selectedOption
                                                    ? selectedOption.value
                                                    : "",
                                            }));
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                responsible_person: "",
                                            }));
                                        }}
                                        placeholder="Select responsible person"
                                        components={animatedComponents}
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={selectPortalStyles}
                                    />
                                    <ErrorMessage error={fieldErrors.responsible_person} />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">
                                        Client <span className="required">*</span>
                                    </label>
                                    <Select
                                        options={clients}
                                        classNamePrefix="FormClientContract-select"
                                        value={selectedClient}
                                        placeholder="Client from parent contract"
                                        components={{
                                            ...animatedComponents,
                                            DropdownIndicator: () => null,
                                            IndicatorSeparator: () => null,
                                        }}
                                        isClearable={false}
                                        isSearchable={false}
                                        isDisabled menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={selectPortalStyles}
                                    />
                                    <ErrorMessage error={fieldErrors.client} />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Project Code
                                        {!isFrameworkAgreement && (
                                            <>
                                                {" "}
                                                <span className="required">*</span>
                                            </>
                                        )}
                                    </label>
                                    <Select
                                        options={projectCodeOptions}
                                        classNamePrefix="FormClientContract-select"
                                        value={selectedProjectCode}
                                        onChange={handleProjectCodeChange}
                                        placeholder={
                                            isFrameworkAgreement
                                                ? "Select project code"
                                                : "Project code from parent contract"
                                        }
                                        components={
                                            isFrameworkAgreement
                                                ? animatedComponents
                                                : {
                                                    ...animatedComponents,
                                                    DropdownIndicator: () => null,
                                                    IndicatorSeparator: () => null,
                                                }
                                        }
                                        isClearable={isFrameworkAgreement}
                                        isSearchable={isFrameworkAgreement}
                                        isDisabled={!isFrameworkAgreement}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={selectPortalStyles}
                                    />
                                    <ErrorMessage error={fieldErrors.project_code} />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Status <span className="required">*</span>
                                    </label>
                                    <Select
                                        options={STATUS_OPTIONS}
                                        classNamePrefix="FormClientContract-select"
                                        value={selectedStatus}
                                        placeholder="Status from parent contract"
                                        components={{
                                            ...animatedComponents,
                                            DropdownIndicator: () => null,
                                            IndicatorSeparator: () => null,
                                        }}
                                        isClearable={false}
                                        isSearchable={false}
                                        isDisabled
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={selectPortalStyles}
                                    />
                                    <ErrorMessage error={fieldErrors.status} />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">
                                        Sign Date <span className="required">*</span>
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <DatePicker
                                            selected={selectedSignDate}
                                            onChange={(date) => {
                                                setSelectedSignDate(date);
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    sign_date: date ? format(date, "yyyy-MM-dd") : "",
                                                }));
                                                setFieldErrors((prev) => ({ ...prev, sign_date: "" }));
                                            }}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="Select sign date"
                                            className="FormClientContract-date-input"
                                            popperContainer={({ children }) => (
                                                <div style={{ position: "fixed", zIndex: 9999 }}>
                                                    {children}
                                                </div>
                                            )}
                                        />
                                        <CalendarIcon />
                                    </div>
                                    <ErrorMessage error={fieldErrors.sign_date} />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        End Date <span className="required">*</span>
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <DatePicker
                                            selected={selectedEndDate}
                                            onChange={(date) => {
                                                setSelectedEndDate(date);
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    end_date: date ? format(date, "yyyy-MM-dd") : "",
                                                }));
                                                setFieldErrors((prev) => ({ ...prev, end_date: "" }));
                                            }}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="Select end date"
                                            className="FormClientContract-date-input"
                                            popperContainer={({ children }) => (
                                                <div style={{ position: "fixed", zIndex: 9999 }}>
                                                    {children}
                                                </div>
                                            )}
                                        />
                                        <CalendarIcon />
                                    </div>
                                    <ErrorMessage error={fieldErrors.end_date} />
                                </div>
                            </div>

                            <div className="form-group full-width">
                                <label className="form-label">
                                    Object <span className="required">*</span>
                                </label>
                                <textarea
                                    value={formData.object}
                                    onChange={(e) => {
                                        setFormData((prev) => ({ ...prev, object: e.target.value }));
                                        setFieldErrors((prev) => ({ ...prev, object: "" }));
                                    }}
                                    className="FormClientContract-description"
                                    rows={3}
                                    placeholder="Enter addendum object..."
                                />
                                <ErrorMessage error={fieldErrors.object} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="FormClientContract-section">
                    <SectionHeader title="OPPORTUNITIES" section="opportunities" />

                    {expandedSections.opportunities && (
                        <div className="section-content">
                            <div className="form-group full-width">
                                <label className="form-label">
                                    Select Opportunities
                                    {!isFrameworkAgreement && (
                                        <>
                                            {" "}
                                            <span className="required">*</span>
                                        </>
                                    )}
                                </label>

                                <Select
                                    isMulti
                                    options={filteredOpportunitiesByProjectCode}
                                    classNamePrefix="FormClientContract-select"
                                    value={selectedOpps}
                                    onChange={handleOppsMultiChange}
                                    placeholder={
                                        !formData.project_code
                                            ? isFrameworkAgreement
                                                ? "No Project Code assigned"
                                                : "Select project code first"
                                            : isOppLoading
                                                ? "Loading opportunities..."
                                                : "Search and select opportunities..."
                                    }
                                    components={animatedComponents}
                                    isDisabled={!formData.project_code || isOppLoading}
                                    noOptionsMessage={() => "No opportunities found"}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={selectPortalStyles}
                                />

                                <ErrorMessage error={fieldErrors.lines} />
                            </div>

                            {lines.length === 0 ? (
                                <div className="no-opportunities-message">
                                    <AddIcon className="hint-icon" />
                                    <span>No opportunities selected yet</span>
                                </div>
                            ) : (
                                <div className="opportunities-grid">
                                    {lines.map((line, idx) => {
                                        const currencyId = line.currency?.value ?? null;
                                        const exchangeOpts = currencyId
                                            ? exchangeOptionsByCurrencyId.get(currencyId) || []
                                            : [];

                                        return (
                                            <div key={line.opportunityId} className="opportunity-item">
                                                <div className="opportunity-item-header">
                                                    <div className="item-title">
                                                        <h3>{line.opportunityLabel}</h3>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="remove-btn"
                                                        onClick={() => removeOpportunity(line.opportunityId)}
                                                        title="Remove opportunity"
                                                    >
                                                        <DeleteIcon />
                                                    </button>
                                                </div>

                                                <div className="opportunity-details">
                                                    <div className="detail-row single-detail-row">
                                                        <div className="detail-field">
                                                            <label className="form-label">Subproject Code</label>
                                                            <input
                                                                type="text"
                                                                value={line.subproject_code_text}
                                                                readOnly
                                                                className="readonly-field"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="pricing-row">
                                                        <div className="pricing-field">
                                                            <label className="form-label">Total</label>
                                                            <NumericFormat
                                                                value={line.total}
                                                                onValueChange={(values) => {
                                                                    const v =
                                                                        values.floatValue !== undefined
                                                                            ? values.floatValue
                                                                            : null;
                                                                    updateLine(line.opportunityId, { total: v });
                                                                }}
                                                                thousandSeparator="."
                                                                decimalSeparator=","
                                                                decimalScale={2}
                                                                allowNegative={false}
                                                                placeholder="0,00"
                                                                className="amount-input"
                                                            />
                                                            <ErrorMessage error={fieldErrors[`line_${idx}_total`]} />
                                                        </div>

                                                        <div className="pricing-field">
                                                            <label className="form-label">Currency</label>
                                                            <Select
                                                                options={currencies}
                                                                classNamePrefix="FormClientContract-select"
                                                                value={line.currency}
                                                                onChange={(opt) =>
                                                                    updateLine(line.opportunityId, { currency: opt })
                                                                }
                                                                placeholder="Select"
                                                                components={animatedComponents}
                                                                isClearable
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                styles={selectPortalStyles}
                                                            />
                                                            <ErrorMessage error={fieldErrors[`line_${idx}_currency`]} />
                                                        </div>

                                                        <div className="pricing-field">
                                                            <label className="form-label">Exchange Rate</label>
                                                            <Select
                                                                options={exchangeOpts}
                                                                classNamePrefix="FormClientContract-select"
                                                                value={line.exchange_rate}
                                                                onChange={(opt) =>
                                                                    updateLine(line.opportunityId, {
                                                                        exchange_rate: opt,
                                                                    })
                                                                }
                                                                placeholder={
                                                                    !line.currency
                                                                        ? "Select currency first"
                                                                        : Number(currencyId) === Number(systemCurrencyId)
                                                                            ? "Not needed"
                                                                            : exchangeOpts.length
                                                                                ? "Select rate"
                                                                                : "No rates"
                                                                }
                                                                components={animatedComponents}
                                                                isClearable
                                                                isDisabled={
                                                                    !line.currency ||
                                                                    Number(currencyId) === Number(systemCurrencyId)
                                                                }
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                formatOptionLabel={(option, { context }) =>
                                                                    context === "value"
                                                                        ? option.label
                                                                        : `${option.label}${option.dateLabel
                                                                            ? ` (${formatDateLabel(option.dateLabel)})`
                                                                            : ""
                                                                        }`
                                                                }
                                                                styles={selectPortalStyles}
                                                            />
                                                            <ErrorMessage
                                                                error={fieldErrors[`line_${idx}_exchange_rate`]}
                                                            />
                                                        </div>

                                                        <div className="pricing-field">
                                                            <label className="form-label">
                                                                System Total
                                                                {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                                                            </label>
                                                            <NumericFormat
                                                                value={line.system_total}
                                                                thousandSeparator="."
                                                                decimalSeparator=","
                                                                decimalScale={2}
                                                                placeholder="0,00"
                                                                className="amount-input"
                                                                readOnly
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="FormClientContract-section">
                    <SectionHeader title="ATTACHMENTS" section="attachments" />

                    {expandedSections.attachments && (
                        <div className="section-content">
                            <div className="form-group full-width">
                                <label className="form-label">Attachments</label>

                                {existingAttachments.length > 0 && (
                                    <div className="files-preview-container" style={{ marginBottom: "14px" }}>
                                        <h4>Current Files ({existingAttachments.length})</h4>

                                        <div className="files-list">
                                            {existingAttachments.map((attachment, index) => (
                                                <div
                                                    key={attachment.id || index}
                                                    className="file-preview-item existing"
                                                >
                                                    <div className="file-info">
                                                        {getFileIcon(attachment, true)}

                                                        <span
                                                            className="file-name clickable"
                                                            onClick={() => handleFileClick(attachment, true)}
                                                        >
                                                            {getExistingFileName(attachment)}
                                                        </span>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="remove-file-button"
                                                        onClick={() => handleRemoveFile(index, true)}
                                                        title="Remove file"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div key={dropzoneKey} className="dropzone-wrapper">
                                    <div {...getRootProps({ className: "dropzone" })}>
                                        <input {...getInputProps()} />

                                        <div className="dropzone-content">
                                            {isDragActive ? (
                                                <p>Drop the files here...</p>
                                            ) : (
                                                <p>Drag & drop files or click to select</p>
                                            )}

                                            <CloudUploadIcon style={{ fontSize: 40, color: "#888" }} />
                                        </div>
                                    </div>

                                    {files.length > 0 && (
                                        <div className="files-preview-container">
                                            <h4>New Files ({files.length})</h4>

                                            <div className="files-list">
                                                {files.map((file, index) => (
                                                    <div key={index} className="file-preview-item">
                                                        <div className="file-info">
                                                            {getFileIcon(file)}

                                                            <span
                                                                className="file-name clickable"
                                                                onClick={() => handleFileClick(file)}
                                                            >
                                                                {file.name.length > 40
                                                                    ? `${file.name.substring(0, 40)}...`
                                                                    : file.name}
                                                            </span>

                                                            <span className="file-size">
                                                                {(file.size / 1024).toFixed(1)} KB
                                                            </span>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            className="remove-file-button"
                                                            onClick={() => handleRemoveFile(index)}
                                                            title="Remove file"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <p className="file-hint">
                                    Accepted file types: PDF, Word, Excel, Images, Text. Multiple files
                                    allowed (max 100MB per file).
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="contract-total-preview">
                    <div className="preview-line">
                        <span className="contract-total-label">Total Addendum:</span>
                        <span className="contract-total-value">{totalAddendumDisplay}</span>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="secondary-btn"
                        onClick={() => navigate(-1)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>

                    <button type="submit" className="primary-btn" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span className="spinner"></span>
                                Saving...
                            </>
                        ) : (
                            "Update"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditClientAddendum;